// ── Tool API client ──────────────────────────────────────────────
// Thin wrapper around the Digital Products API Tool module
// (see http://localhost:8400 → Tool).

import { request, requestWithMeta } from "@/lib/api/client";
import {
  cachedRequest,
  invalidateGenerationsAndProjects,
} from "@/lib/api/cache";
import type { Tool, ToolField, ToolFieldType, ToolRunArtifact } from "@/lib/types";

export type ToolRunFieldValue = string | number | boolean;
export type ToolRunValues = Record<string, ToolRunFieldValue>;
export type ToolRunModality = "text" | "image" | "speech";

export interface ApiToolRunArtifact {
  role: "preview" | "download" | "stream";
  url: string;
  fileName: string;
  mimeType: string;
  extension: string;
}

export interface ApiToolRunTextResult {
  type: "text";
  markdown: string;
  artifacts: ApiToolRunArtifact[];
}

export interface ApiToolRunImageResult {
  type: "image";
  artifacts: ApiToolRunArtifact[];
}

export interface ApiToolRunSpeechResult {
  type: "speech";
  artifacts: ApiToolRunArtifact[];
}

export type ApiToolRunResult =
  | ApiToolRunTextResult
  | ApiToolRunImageResult
  | ApiToolRunSpeechResult;

export interface ApiToolRunData {
  modality: ToolRunModality;
  /** Public history slug for GET /v1/generation/{slug}. */
  generationSlug: string;
  result: ApiToolRunResult;
}

export interface ApiToolRunMeta {
  requestId: string;
  remaining?: number;
  limit?: number;
  period?: "lifetime" | "month" | "billing_period";
}

export interface ToolRunRequestBody {
  values: ToolRunValues;
  /** Required for authenticated users; omit for guests. */
  projectSlug?: string;
  /** Draft generation title; required for authenticated users, omit for guests. */
  title?: string;
}

export interface ApiToolCategory {
  slug: string;
  name: string;
}

/** Localized summary from GET /v1/tool `data[]`. */
export interface ApiToolListItem {
  slug: string;
  category: ApiToolCategory;
  icon: string;
  isPro: boolean;
  sortOrder: number;
  title: string;
  description: string | null;
}

export interface ApiToolFieldOption {
  id: number;
  label: string;
  isDefault: boolean;
}

/** One field from GET /v1/tool/{slug} `fields[]`. */
export interface ApiToolField {
  slug: string;
  type: ToolFieldType;
  label: string;
  placeholder: string | null;
  helpText: string | null;
  isRequired: boolean;
  isPro: boolean;
  isExperimental: boolean;
  sortOrder: number;
  options: ApiToolFieldOption[];
  isDefaultChecked?: boolean;
}

/** Localized detail from GET /v1/tool/{slug}. */
export interface ApiToolDetail extends ApiToolListItem {
  fields: ApiToolField[];
}

export function mapApiToolField(field: ApiToolField): ToolField {
  return {
    slug: field.slug,
    type: field.type,
    label: field.label,
    placeholder: field.placeholder ?? undefined,
    helpText: field.helpText ?? undefined,
    isRequired: field.isRequired,
    isPro: field.isPro,
    isExperimental: field.isExperimental,
    sortOrder: field.sortOrder,
    options: field.options ?? [],
    isDefaultChecked: field.isDefaultChecked,
  };
}

export function mapApiTool(item: ApiToolListItem): Tool {
  return {
    name: item.title,
    slug: item.slug,
    category: item.category.name,
    categorySlug: item.category.slug,
    desc: item.description ?? "",
    isPro: item.isPro,
    icon: item.icon || undefined,
  };
}

/** Map a detail payload, including sorted `fields`. */
export function mapApiToolDetail(item: ApiToolDetail): Tool {
  const fields = [...(item.fields ?? [])]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map(mapApiToolField);
  return {
    ...mapApiTool(item),
    fields,
  };
}

/** True when `icon` looks like a remote/relative image path rather than an emoji. */
export function isToolIconUrl(icon: string): boolean {
  return (
    /^(https?:\/\/|\/)/i.test(icon) ||
    /\.(png|jpe?g|gif|webp|svg)(\?|#|$)/i.test(icon)
  );
}

/** Resolve a relative tool icon path against the API origin. */
export function resolveToolIconUrl(icon: string): string {
  if (/^https?:\/\//i.test(icon)) return icon;
  const base = process.env.NEXT_PUBLIC_API_BASE_URL?.replace(/\/+$/, "") ?? "";
  if (icon.startsWith("/")) return `${base}${icon}`;
  return icon;
}

/**
 * GET /v1/tool — list active tools (localized via Accept-Language).
 * Cached per locale: the catalog is static content and the grid remounts on
 * every return to /tools.
 */
export async function fetchTools(locale?: string): Promise<ApiToolListItem[]> {
  return cachedRequest(`tools:${locale ?? ""}`, async () => {
    const data = await request<ApiToolListItem[]>("/v1/tool", {
      method: "GET",
      headers: locale ? { "Accept-Language": locale } : undefined,
    });
    return data ?? [];
  });
}

/** GET /v1/tool/{slug} — show one active tool (localized), including fields. */
export async function fetchTool(
  slug: string,
  locale?: string,
): Promise<ApiToolDetail> {
  return cachedRequest(`tool:${locale ?? ""}:${slug}`, () =>
    request<ApiToolDetail>(`/v1/tool/${encodeURIComponent(slug)}`, {
      method: "GET",
      headers: locale ? { "Accept-Language": locale } : undefined,
    }),
  );
}

/**
 * Build `values` for POST /v1/tool/{slug}/run.
 * Pro fields are omitted when the caller has no active Pro subscription.
 * Unchecked optional checkboxes and empty input/select values are omitted.
 */
export function buildToolRunValues(
  fields: ToolField[],
  formValues: Record<string, string | boolean>,
  hasPro: boolean,
): ToolRunValues {
  const values: ToolRunValues = {};

  for (const field of fields) {
    if (!hasPro && field.isPro) continue;

    const raw = formValues[field.slug];

    if (field.type === "checkbox") {
      if (raw === true) values[field.slug] = true;
      continue;
    }

    const text = String(raw ?? "").trim();
    if (!text) continue;

    if (field.type === "select") {
      const optionId = Number.parseInt(text, 10);
      if (!Number.isNaN(optionId)) values[field.slug] = optionId;
      continue;
    }

    values[field.slug] = text;
  }

  return values;
}

export function mapToolRunArtifacts(
  artifacts: ApiToolRunArtifact[] | undefined,
): ToolRunArtifact[] {
  return (artifacts ?? []).map((a) => ({
    role: a.role,
    url: a.url,
    fileName: a.fileName,
    mimeType: a.mimeType,
    extension: a.extension,
  }));
}

/** POST /v1/tool/{slug}/run — execute a tool with submitted field values. */
export async function runTool(
  slug: string,
  body: ToolRunRequestBody,
  options?: {
    locale?: string;
    signal?: AbortSignal;
  },
): Promise<{ data: ApiToolRunData; meta: ApiToolRunMeta }> {
  const headers: HeadersInit = {
    ...(options?.locale ? { "Accept-Language": options.locale } : {}),
  };

  return requestWithMeta<ApiToolRunData, ApiToolRunMeta>(
    `/v1/tool/${encodeURIComponent(slug)}/run`,
    {
      method: "POST",
      headers,
      body: JSON.stringify(body),
      signal: options?.signal,
    },
  ).then((result) => {
    // A new generation exists and its project's counts changed.
    invalidateGenerationsAndProjects();
    return result;
  });
}
